// The in-memory twin of WOS-BUNDLE's second half (Work OS parity items 6, 11, 16): duplicate a
// task, a board or a space; My Tasks sections and where the person put each task; task time with
// one running timer per person; a board's time total; the usage CSV. The rules are the contracts'
// (contracts/api/work-bundle.ts) and Work OS's (`duplicateTask`, `duplicateContainerTree`,
// `setMyTaskBucket`, `domain/time.ts`): a copy is "<title> (copy)" next to the original with its
// checklists reopened; a board or space copy is "Copy of <name>" with statuses, fields, views and
// settings, tasks only when asked; a section name must not be empty and an unknown section is a
// 422; starting a timer stops (and books) the one running; a stop books at least a minute.
// Webhook pause and scope live with the webhooks twin (fake-webhooks.ts).
// `fake-work-bundle-2-contract.test.ts` holds it to the contracts.
import type { OperationOutput, RouteId } from "@companyos/sdk";
import { type Ctx, type Handler, json, notFound, type Principal, peekJson, problem } from "./fake";
import { type FakeDe, uid } from "./fake-data";
import type { WorkBridge } from "./fake-work";
import type { Board, FieldDef, Space, StoredTask, View } from "./fake-work-data";

type TimeEntry = OperationOutput<"tasks.logTime">;
type TimerView = NonNullable<OperationOutput<"timer.get">["timer"]>;
type Section = OperationOutput<"mySections.create">;
type Placement = OperationOutput<"my.placeTask">;
type WorkState = NonNullable<ReturnType<WorkBridge["stateOf"]>>;

/** Work OS's built-in buckets, in their order (CompanyOS calls `next_week` "Upcoming"). */
export const BUILTIN_SECTIONS = [
  { id: "recent", name: "Recently assigned" },
  { id: "today", name: "Today" },
  { id: "upcoming", name: "Upcoming" },
  { id: "later", name: "Later" },
] as const;
const BUILTIN = new Set<string>(BUILTIN_SECTIONS.map((s) => s.id));

type Timer = {
  company_id: string;
  membership_id: string;
  task_id: string;
  seat_id: string;
  started_at: string;
  note: string | null;
};

const validation = (detail: string) =>
  problem(422, "validation_failed", "Validation failed", detail);
const forbidden = (detail?: string) => problem(403, "forbidden", "Forbidden", detail);

export function createWorkBundle2Fake(deps: {
  now: () => Date;
  desOf: (companyId: string) => FakeDe[];
  work: () => WorkBridge | null;
  /** The Work twin's own handlers (`spaces.create`, `boards.create`, `tasks.get`). */
  workHandlers: () => Partial<Record<RouteId, Handler>>;
  /** TASK-FILES' twin: copies a task's files onto its copy (`files: true`). */
  copyFiles?: (
    companyId: string,
    fromTaskId: string,
    toTaskId: string,
    actor: Parameters<WorkBridge["recordTaskEvent"]>[4],
  ) => void;
}): Partial<Record<RouteId, Handler>> {
  let seq = 0;
  const next = (group: number) => uid(group, ++seq);
  const stamp = () => deps.now().toISOString();
  const sections = new Map<string, Section[]>();
  const placements = new Map<string, Placement[]>();
  const timers = new Map<string, Timer>();
  const entries = new Map<string, TimeEntry[]>();
  const seeded = new Set<string>();

  const membershipOf = (p: Principal) =>
    p.me.memberships.find((m) => m.company_id === p.company.id)?.membership_id ?? null;
  /** Client viewers see no work (as the Work twin). */
  const reader = (p: Principal | null): p is Principal =>
    p?.roles.some((r) => r !== "client_viewer") === true;
  /** The caller's seat: the one its membership holds (the seeded supervisors), as `my.tasks`. */
  const seatOf = (companyId: string, membership: string | null) =>
    deps.desOf(companyId).find((d) => d.supervisor.membership_id === membership)?.supervisor
      .seat_id ?? null;
  const stateOf = (p: Principal): WorkState | null => deps.work()?.stateOf(p.company.id) ?? null;
  const taskIn = (p: Principal, id: string | undefined) =>
    stateOf(p)?.tasks.find((t) => t.id === id && !t.deleted) ?? null;
  const call = (id: RouteId, ctx: Ctx) => deps.workHandlers()[id]?.(ctx) ?? notFound();
  const key = (p: Principal, m: string) => `${p.company.id}:${m}`;
  const entriesOf = (companyId: string) => {
    const l = entries.get(companyId) ?? [];
    entries.set(companyId, l);
    return l;
  };

  // Seed: My Tasks sections and placements for each supervisor, time on some of their tasks ----

  function seed(p: Principal) {
    if (seeded.has(p.company.id)) return;
    const s = stateOf(p);
    if (!s) return;
    seeded.add(p.company.id);
    const today = stamp().slice(0, 10);
    const sups = new Map(deps.desOf(p.company.id).map((d) => [d.supervisor.membership_id, d]));
    for (const [m, de] of sups) {
      const mine = s.tasks.filter(
        (t) => !t.deleted && !t.completed_at && t.assignee_seat_ids.includes(de.supervisor.seat_id),
      );
      const own: Section = {
        company_id: p.company.id,
        id: next(0xd210),
        membership_id: m,
        name: "Waiting on others",
        position: 0,
        created_at: stamp(),
        updated_at: stamp(),
      };
      sections.set(`${p.company.id}:${m}`, [own]);
      const plan = ["today", "today", "upcoming", own.id, "later"];
      placements.set(
        `${p.company.id}:${m}`,
        mine.slice(0, plan.length).map((t, i) => ({
          company_id: p.company.id,
          membership_id: m,
          task_id: t.id,
          section: plan[i] as string,
          position: plan.slice(0, i).filter((x) => x === plan[i]).length,
          updated_at: stamp(),
        })),
      );
      // Time: two entries on the first task, one on the second (one still pending approval).
      const [a, b] = mine;
      const log = entriesOf(p.company.id);
      if (a) {
        log.push(entryRow(p.company.id, m, de.supervisor.seat_id, a.id, 45 * 60, "timer", today));
        log.push(
          entryRow(p.company.id, m, de.supervisor.seat_id, a.id, 30 * 60, "manual", today, {
            note: "Call notes",
          }),
        );
      }
      if (b)
        log.push(
          entryRow(p.company.id, m, de.supervisor.seat_id, b.id, 90 * 60, "manual", today, {
            approval: "pending",
            note: "Interview loop",
          }),
        );
    }
  }

  function entryRow(
    companyId: string,
    membership: string,
    seat: string,
    taskId: string,
    seconds: number,
    source: "timer" | "manual",
    workDate: string,
    o: { note?: string | null; started?: string | null; approval?: "approved" | "pending" } = {},
  ): TimeEntry {
    const at = stamp();
    const n = ++seq;
    const ended = o.started ? at : null;
    return {
      company_id: companyId,
      id: uid(0xd220, n),
      seat_id: seat,
      membership_id: membership,
      kind: "work",
      grain: "interval",
      work_date: workDate,
      period_key: workDate,
      started_at: o.started ?? null,
      ended_at: ended,
      duration_seconds: seconds,
      minutes: Math.round(seconds / 60),
      source,
      source_event_id: `${source}:fake:${n}`,
      revision: 1,
      supersedes_id: null,
      superseded_by: null,
      voided_at: null,
      approval_status: o.approval ?? "approved",
      decided_by_membership_id: null,
      decided_at: (o.approval ?? "approved") === "approved" ? at : null,
      rejection_reason: null,
      source_ref: null,
      source_actor_ref: null,
      dims: null,
      stats: null,
      visibility: "client",
      note: o.note ?? null,
      task_id: taskId,
      actor_kind: "person",
      actor_membership_id: membership,
      created_at: at,
    };
  }

  // Duplicate --------------------------------------------------------------------------------

  type CopyOptions = {
    subtasks: boolean;
    checklists: boolean;
    assignees: boolean;
    /** TASK-FILES: the task's files and folders (a task copy, when asked). */
    files?: boolean;
  };

  /** Copies one task (and, when asked, its subtasks) into `boardId`; returns the copy. */
  function copyTask(
    p: Principal,
    s: WorkState,
    t: StoredTask,
    o: CopyOptions & {
      boardId: string;
      title: string;
      parent: string | null;
      fieldMap?: Map<string, string>;
    },
  ): { task: StoredTask; subtasks: number } {
    const at = stamp();
    const id = next(0xd200);
    const listMap = new Map<string, string>();
    const checklists = o.checklists
      ? t.checklists.map((l) => {
          const nid = next(0xd250);
          listMap.set(l.id, nid);
          return { ...l, id: nid, task_id: id, created_at: at, updated_at: at };
        })
      : [];
    const items = o.checklists
      ? t.checklist_items.map((i) => ({
          ...i,
          id: next(0xd260),
          task_id: id,
          checklist_id: listMap.get(i.checklist_id) ?? i.checklist_id,
          // Work OS reopens a copied checklist.
          done_at: null,
          done_by_seat_id: null,
          created_at: at,
          updated_at: at,
        }))
      : [];
    const fields: Record<string, unknown> = {};
    for (const [f, v] of Object.entries(t.fields)) fields[o.fieldMap?.get(f) ?? f] = v;
    const copy: StoredTask = {
      ...t,
      id,
      board_id: o.boardId,
      number: s.tasks.reduce((n, x) => Math.max(n, x.number), 0) + 1,
      title: o.title,
      parent_task_id: o.parent,
      page_id: null,
      thread_id: null,
      client_id: null,
      origin: "duplicate",
      origin_ref: { task_id: t.id },
      completed_at: t.completed_at ? at : null,
      version: 1,
      actor_kind: "person",
      actor_membership_id: membershipOf(p),
      created_at: at,
      updated_at: at,
      tag_ids: [...t.tag_ids],
      assignee_seat_ids: o.assignees ? [...t.assignee_seat_ids] : [],
      fields,
      checklists,
      checklist_items: items,
      dependencies: [],
    };
    // Next to the original (Work OS: "is right next to it").
    const at0 = s.tasks.indexOf(t);
    s.tasks.splice(at0 >= 0 && t.board_id === o.boardId ? at0 + 1 : s.tasks.length, 0, copy);
    if (o.files) {
      const m = membershipOf(p);
      deps.copyFiles?.(
        p.company.id,
        t.id,
        copy.id,
        m ? { kind: "person", membership_id: m } : { kind: "system" },
      );
    }
    let subtasks = 0;
    if (o.subtasks)
      for (const child of s.tasks.filter((x) => x.parent_task_id === t.id && !x.deleted)) {
        if (child.id === copy.id) continue;
        const made = copyTask(p, s, child, { ...o, title: child.title, parent: copy.id });
        subtasks += 1 + made.subtasks;
      }
    return { task: copy, subtasks };
  }

  /** A board's copy in `spaceId`: statuses, settings, fields, views; its tasks when asked. */
  function copyBoard(
    ctx: Ctx,
    p: Principal,
    s: WorkState,
    b: Board,
    spaceId: string,
    name: string,
    withTasks: boolean,
  ): Board | Response {
    const res = call("boards.create", {
      ...ctx,
      params: { co: p.company.id },
      body: {
        space_id: spaceId,
        name,
        template: "blank",
        status_set: b.status_set,
        ...(b.color ? { color: b.color } : {}),
        ...(b.lead_seat_id ? { lead_seat_id: b.lead_seat_id } : {}),
      },
    });
    const made = peekJson<Board>(res);
    if (!made) return res;
    const board = s.boards.find((x) => x.id === made.id) as Board;
    const at = stamp();
    const fieldMap = new Map<string, string>();
    for (const f of s.fields.filter((x) => x.board_id === b.id)) {
      const nf: FieldDef = { ...f, id: next(0xd230), board_id: board.id, created_at: at };
      fieldMap.set(f.id, nf.id);
      s.fields.push(nf);
    }
    // The copy's views are the original's (field ids mapped); its first two made by create go.
    const remap = <T>(v: T): T => {
      let text = JSON.stringify(v);
      for (const [from, to] of fieldMap) text = text.split(from).join(to);
      return JSON.parse(text) as T;
    };
    const ownViews = s.views.filter((v) => v.board_id === board.id);
    for (const v of ownViews) s.views.splice(s.views.indexOf(v), 1);
    const viewMap = new Map<string, string>();
    for (const v of s.views.filter((x) => x.board_id === b.id)) {
      const nv: View = {
        ...remap(v),
        id: next(0xd240),
        board_id: board.id,
        created_at: at,
        updated_at: at,
      };
      viewMap.set(v.id, nv.id);
      s.views.push(nv);
    }
    board.settings = {
      ...remap(b.settings),
      ...(b.settings.default_view_id
        ? { default_view_id: viewMap.get(b.settings.default_view_id) ?? null }
        : {}),
    } as Board["settings"];
    board.digest_interval = b.digest_interval;
    if (withTasks)
      for (const t of s.tasks.filter(
        (x) => x.board_id === b.id && !x.deleted && x.parent_task_id === null,
      ))
        copyTask(p, s, t, {
          boardId: board.id,
          title: t.title,
          parent: null,
          subtasks: true,
          checklists: true,
          assignees: true,
          fieldMap,
        });
    return board;
  }

  const bool = (v: unknown, fallback: boolean) => (typeof v === "boolean" ? v : fallback);
  const nameOf = (v: unknown, fallback: string, max: number) => {
    if (v === undefined) return fallback;
    const n = typeof v === "string" ? v.trim() : "";
    return n && n.length <= max ? n : null;
  };

  // Time ---------------------------------------------------------------------------------------

  /** Read-only agent time: the run time of digital employees on the task (seeded per task). */
  function agentSeconds(p: Principal, t: StoredTask): number {
    const des = new Set(deps.desOf(p.company.id).map((d) => d.seat_id));
    return t.assignee_seat_ids.some((x) => des.has(x)) ? (((t.number * 7) % 40) + 12) * 60 : 0;
  }

  function timerView(p: Principal, timer: Timer): TimerView | null {
    const t = taskIn(p, timer.task_id);
    return t
      ? {
          task_id: t.id,
          task_number: t.number,
          task_title: t.title,
          board_id: t.board_id,
          seat_id: timer.seat_id,
          started_at: timer.started_at,
          note: timer.note,
        }
      : null;
  }

  /** Stops the caller's timer and books it (at least a minute; Work OS `max(1, round(min))`). */
  function stopTimer(p: Principal, m: string, note?: string): TimeEntry | null {
    const timer = timers.get(key(p, m));
    if (!timer) return null;
    timers.delete(key(p, m));
    const elapsed = Math.round((deps.now().getTime() - Date.parse(timer.started_at)) / 1000);
    const seconds = Math.max(60, elapsed);
    const e = entryRow(
      p.company.id,
      m,
      timer.seat_id,
      timer.task_id,
      seconds,
      "timer",
      timer.started_at.slice(0, 10),
      { note: note ?? timer.note, started: timer.started_at },
    );
    entriesOf(p.company.id).push(e);
    return e;
  }

  const live = (e: TimeEntry) => e.voided_at === null;
  const booked = (e: TimeEntry) => live(e) && e.approval_status !== "rejected";

  // Usage CSV ----------------------------------------------------------------------------------

  const DAY = /^\d{4}-\d{2}-\d{2}$/;
  const csvCell = (v: string | number) =>
    typeof v === "number" || !/[",\n]/.test(v) ? String(v) : `"${v.replaceAll('"', '""')}"`;

  function usageCsv(p: Principal, from: string, to: string): string {
    const rows: (string | number)[][] = [
      [
        "date",
        "kind",
        "seat",
        "agent",
        "run",
        "model",
        "input_tokens",
        "output_tokens",
        "amount",
        "currency",
      ],
    ];
    const des = deps.desOf(p.company.id);
    const end = Date.parse(`${to}T00:00:00Z`);
    let n = 0;
    for (let d = Date.parse(`${from}T00:00:00Z`); d <= end; d += 86_400_000) {
      const day = new Date(d).toISOString().slice(0, 10);
      for (const de of des) {
        n += 1;
        const input = 12_000 + ((n * 977) % 9000);
        const output = 1_800 + ((n * 313) % 1500);
        rows.push([
          day,
          "model_usage",
          de.seat_title,
          de.display_name,
          uid(0xd270, n),
          "claude-sonnet",
          input,
          output,
          ((input * 3 + output * 15) / 1_000_000).toFixed(4),
          "USD",
        ]);
      }
    }
    return `${rows.map((r) => r.map(csvCell).join(",")).join("\n")}\n`;
  }

  // Handlers -----------------------------------------------------------------------------------

  return {
    "tasks.duplicate": (ctx) => {
      const p = ctx.principal;
      if (!reader(p)) return notFound();
      const s = stateOf(p);
      const t = taskIn(p, ctx.params.id);
      if (!s || !t) return notFound();
      const b = (ctx.body ?? {}) as Record<string, unknown>;
      const title = nameOf(b.title, `${t.title} (copy)`, 500);
      if (title === null) return validation("title must be 1–500 characters");
      const made = copyTask(p, s, t, {
        boardId: t.board_id,
        title,
        parent: t.parent_task_id,
        subtasks: bool(b.subtasks, false),
        checklists: bool(b.checklists, true),
        assignees: bool(b.assignees, false),
        files: bool(b.files, false),
      });
      const res = call("tasks.get", { ...ctx, params: { co: p.company.id, id: made.task.id } });
      const detail = peekJson(res);
      return detail ? json(detail, 201) : res;
    },
    "boards.duplicate": (ctx) => {
      const p = ctx.principal;
      if (!reader(p)) return notFound();
      const s = stateOf(p);
      const b = s?.boards.find((x) => x.id === ctx.params.id);
      if (!s || !b) return notFound();
      const body = (ctx.body ?? {}) as Record<string, unknown>;
      const name = nameOf(body.name, `Copy of ${b.name}`, 120);
      if (name === null) return validation("name must not be empty");
      const made = copyBoard(ctx, p, s, b, b.space_id, name, bool(body.tasks, false));
      return made instanceof Response ? made : json(made, 201);
    },
    "spaces.duplicate": (ctx) => {
      const p = ctx.principal;
      if (!reader(p)) return notFound();
      const s = stateOf(p);
      const sp = s?.spaces.find((x) => x.id === ctx.params.id);
      if (!s || !sp) return notFound();
      if (sp.kind === "personal") return validation("a personal space cannot be copied");
      const body = (ctx.body ?? {}) as Record<string, unknown>;
      const name = nameOf(body.name, `Copy of ${sp.name}`, 120);
      if (name === null) return validation("name must not be empty");
      const res = call("spaces.create", {
        ...ctx,
        params: { co: p.company.id },
        body: {
          name,
          kind: sp.kind,
          team_id: sp.team_id,
          icon: sp.icon,
          description: sp.description,
          is_private: sp.is_private,
          settings: sp.settings,
        },
      });
      const space = peekJson<Space>(res);
      if (!space) return res;
      const withTasks = bool(body.tasks, false);
      for (const b of s.boards
        .filter((x) => x.space_id === sp.id && !x.archived_at)
        .sort((x, y) => x.position - y.position)) {
        const made = copyBoard(ctx, p, s, b, space.id, b.name, withTasks);
        if (made instanceof Response) return made;
      }
      return json(space, 201);
    },

    // My Tasks sections --------------------------------------------------------------------

    "mySections.list": ({ principal: p }) => {
      if (!reader(p)) return notFound();
      seed(p);
      const m = membershipOf(p);
      const own = m ? (sections.get(key(p, m)) ?? []) : [];
      const visible = new Set((stateOf(p)?.tasks ?? []).filter((t) => !t.deleted).map((t) => t.id));
      return json({
        sections: [
          ...BUILTIN_SECTIONS.map((b, i) => ({ ...b, builtin: true, position: i })),
          ...[...own]
            .sort((a, b) => a.position - b.position)
            .map((x) => ({ id: x.id, name: x.name, builtin: false, position: x.position })),
        ],
        placements: (m ? (placements.get(key(p, m)) ?? []) : [])
          .filter((x) => visible.has(x.task_id))
          .map(({ task_id, section, position }) => ({ task_id, section, position })),
      });
    },
    "mySections.create": ({ principal: p, body }) => {
      if (!reader(p)) return notFound();
      seed(p);
      const m = membershipOf(p);
      if (!m) return forbidden("people only");
      const name = String((body as { name?: unknown } | null)?.name ?? "").trim();
      if (!name) return validation("The section needs a name");
      if (name.length > 80) return validation("name: at most 80 characters");
      const list = sections.get(key(p, m)) ?? [];
      sections.set(key(p, m), list);
      const at = stamp();
      const row: Section = {
        company_id: p.company.id,
        id: next(0xd210),
        membership_id: m,
        name,
        position: list.length,
        created_at: at,
        updated_at: at,
      };
      list.push(row);
      return json(row, 201);
    },
    "mySections.update": ({ principal: p, params, body }) => {
      if (!reader(p)) return notFound();
      seed(p);
      const m = membershipOf(p);
      const list = m ? (sections.get(key(p, m)) ?? []) : [];
      const row = list.find((x) => x.id === params.id);
      if (!row) return notFound();
      const b = (body ?? {}) as { name?: unknown; position?: unknown };
      if (b.name !== undefined) {
        const name = String(b.name).trim();
        if (!name) return validation("The section needs a name");
        row.name = name;
      }
      if (b.position !== undefined) {
        const to = Math.max(0, Math.min(list.length - 1, Number(b.position)));
        const ordered = [...list].sort((x, y) => x.position - y.position).filter((x) => x !== row);
        ordered.splice(to, 0, row);
        for (const [i, x] of ordered.entries()) x.position = i;
      }
      row.updated_at = stamp();
      return json(row);
    },
    "mySections.delete": ({ principal: p, params }) => {
      if (!reader(p)) return notFound();
      seed(p);
      const m = membershipOf(p);
      const list = m ? (sections.get(key(p, m)) ?? []) : [];
      const i = list.findIndex((x) => x.id === params.id);
      if (!m || i < 0) return notFound();
      list.splice(i, 1);
      for (const [n, x] of [...list].sort((a, b) => a.position - b.position).entries())
        x.position = n;
      // Its tasks go back to Recently assigned (a task without a placement is there).
      placements.set(
        key(p, m),
        (placements.get(key(p, m)) ?? []).filter((x) => x.section !== params.id),
      );
      return new Response(null, { status: 204 });
    },
    "my.placeTask": ({ principal: p, params, body }) => {
      if (!reader(p)) return notFound();
      seed(p);
      const m = membershipOf(p);
      if (!m) return forbidden("people only");
      const t = taskIn(p, params.id);
      if (!t) return notFound();
      const b = (body ?? {}) as { section?: unknown; position?: unknown };
      const section = String(b.section ?? "");
      if (!BUILTIN.has(section) && !(sections.get(key(p, m)) ?? []).some((x) => x.id === section))
        return validation("Unknown My Tasks section");
      const all = (placements.get(key(p, m)) ?? []).filter((x) => x.task_id !== t.id);
      const inSection = all
        .filter((x) => x.section === section)
        .sort((x, y) => x.position - y.position);
      const to =
        b.position === undefined
          ? inSection.length
          : Math.max(0, Math.min(inSection.length, Number(b.position)));
      const row: Placement = {
        company_id: p.company.id,
        membership_id: m,
        task_id: t.id,
        section,
        position: to,
        updated_at: stamp(),
      };
      inSection.splice(to, 0, row);
      for (const [i, x] of inSection.entries()) x.position = i;
      placements.set(key(p, m), [...all.filter((x) => x.section !== section), ...inSection]);
      return json(row);
    },

    // Time ---------------------------------------------------------------------------------

    "tasks.time": ({ principal: p, params }) => {
      if (!reader(p)) return notFound();
      seed(p);
      const t = taskIn(p, params.id);
      if (!t) return notFound();
      const list = entriesOf(p.company.id)
        .filter((e) => e.task_id === t.id && live(e))
        .sort((a, b) => a.created_at.localeCompare(b.created_at));
      const m = membershipOf(p);
      const timer = m ? timers.get(key(p, m)) : undefined;
      return json({
        entries: list.map((e) => ({
          id: e.id,
          membership_id: e.membership_id,
          seat_id: e.seat_id,
          work_date: e.work_date,
          started_at: e.started_at,
          ended_at: e.ended_at,
          duration_seconds: e.duration_seconds,
          source: e.source,
          approval_status: e.approval_status,
          note: e.note,
          created_at: e.created_at,
        })),
        total_seconds: list.filter(booked).reduce((n, e) => n + e.duration_seconds, 0),
        agent_seconds: agentSeconds(p, t),
        running: timer && timer.task_id === t.id ? timerView(p, timer) : null,
      });
    },
    "tasks.logTime": ({ principal: p, params, body }) => {
      if (!reader(p)) return notFound();
      seed(p);
      const t = taskIn(p, params.id);
      if (!t) return notFound();
      const m = membershipOf(p);
      const seat = seatOf(p.company.id, m);
      if (!m || !seat) return problem(422, "no_seat", "You hold no seat to book this time on");
      const b = (body ?? {}) as { minutes?: unknown; note?: unknown; work_date?: unknown };
      const minutes = Number(b.minutes);
      if (!Number.isInteger(minutes) || minutes < 1 || minutes > 24 * 60)
        return validation("minutes: a whole number from 1 to 1440");
      const day = typeof b.work_date === "string" ? b.work_date : stamp().slice(0, 10);
      if (!DAY.test(day)) return validation("work_date: a day (YYYY-MM-DD)");
      const e = entryRow(p.company.id, m, seat, t.id, minutes * 60, "manual", day, {
        note: typeof b.note === "string" && b.note.trim() ? b.note.trim() : null,
      });
      entriesOf(p.company.id).push(e);
      return json(e, 201);
    },
    "tasks.deleteTime": ({ principal: p, params }) => {
      if (!reader(p)) return notFound();
      seed(p);
      const t = taskIn(p, params.id);
      const log = entriesOf(p.company.id);
      const i = log.findIndex((e) => e.id === params.item_id && e.task_id === t?.id && live(e));
      if (!t || i < 0) return notFound();
      if (log[i]?.membership_id !== membershipOf(p) && !p.manager)
        return forbidden("only your own time, unless you are a manager");
      log.splice(i, 1);
      return new Response(null, { status: 204 });
    },
    "boards.time": ({ principal: p, params }) => {
      if (!reader(p)) return notFound();
      seed(p);
      const s = stateOf(p);
      if (!s?.boards.some((b) => b.id === params.id)) return notFound();
      const tasks = s.tasks.filter((t) => t.board_id === params.id && !t.deleted);
      const ids = new Set(tasks.map((t) => t.id));
      const per = new Map<string, number>();
      for (const e of entriesOf(p.company.id))
        if (e.task_id && ids.has(e.task_id) && booked(e))
          per.set(e.task_id, (per.get(e.task_id) ?? 0) + e.duration_seconds);
      return json({
        total_seconds: [...per.values()].reduce((n, x) => n + x, 0),
        agent_seconds: tasks.reduce((n, t) => n + agentSeconds(p, t), 0),
        tasks: [...per.entries()].map(([task_id, seconds]) => ({ task_id, seconds })),
      });
    },
    "timer.get": ({ principal: p }) => {
      if (!reader(p)) return notFound();
      const m = membershipOf(p);
      const timer = m ? timers.get(key(p, m)) : undefined;
      return json({ timer: timer ? timerView(p, timer) : null });
    },
    "timer.start": ({ principal: p, params }) => {
      if (!reader(p)) return notFound();
      seed(p);
      const t = taskIn(p, params.id);
      if (!t) return notFound();
      const m = membershipOf(p);
      const seat = seatOf(p.company.id, m);
      if (!m || !seat) return problem(422, "no_seat", "You hold no seat to book this time on");
      // One running timer per person: the one running elsewhere is stopped and booked first.
      stopTimer(p, m);
      const timer: Timer = {
        company_id: p.company.id,
        membership_id: m,
        task_id: t.id,
        seat_id: seat,
        started_at: stamp(),
        note: null,
      };
      timers.set(key(p, m), timer);
      return json(timerView(p, timer), 201);
    },
    "timer.stop": ({ principal: p, body }) => {
      if (!reader(p)) return notFound();
      seed(p);
      const m = membershipOf(p);
      const note = (body as { note?: unknown } | null)?.note;
      const e = m ? stopTimer(p, m, typeof note === "string" ? note : undefined) : null;
      return json({ timer: null, time_entry: e });
    },

    // Usage CSV (Reports, managers) ----------------------------------------------------------

    "usage.export": ({ principal: p, url }) => {
      if (!p) return notFound();
      if (!p.manager) return forbidden("managers only");
      const today = stamp().slice(0, 10);
      const from = url.searchParams.get("from") ?? `${today.slice(0, 8)}01`;
      const to = url.searchParams.get("to") ?? today;
      if (!DAY.test(from) || !DAY.test(to)) return validation("from, to: days (YYYY-MM-DD)");
      if (from > to) return validation("from must not be after to");
      if (Date.parse(to) - Date.parse(from) > 366 * 86_400_000)
        return validation("at most a year at a time");
      return new Response(usageCsv(p, from, to), {
        status: 200,
        headers: {
          "content-type": "text/csv; charset=utf-8",
          "content-disposition": `attachment; filename="usage-${from}-to-${to}.csv"`,
        },
      });
    },
  };
}
